// Roles & Access — console-standard rebuild. Team roster with inline editing,
// avatar/colour picker, role + timezone selectors, invites, permissions matrix,
// and recent activity. All team_members CRUD + auth integration preserved.
import React, { useState, useEffect, useRef } from 'react';
import { SCREEN_LABELS, usePresenceContext } from '../hooks/usePresence';
import { ConsoleShell, KPI, Panel, SectionLabel, Empty, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';
import { ROLES, ROLE_LABELS, ROLE_COLORS, ROLE_BG } from '../roles';
import InviteModal, { getPendingInvites, savePendingInvites, removePendingInvite } from '../components/InviteModal';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const TIMEZONES = [
  { label:'UTC  (GMT+0)', value:'UTC' },
  { label:'London, Lisbon  (GMT+0/+1)', value:'Europe/London' },
  { label:'Berlin, Paris, Rome, Madrid  (GMT+1/+2)', value:'Europe/Paris' },
  { label:'Amsterdam, Brussels, Warsaw  (GMT+1/+2)', value:'Europe/Amsterdam' },
  { label:'Helsinki, Kyiv  (GMT+2/+3)', value:'Europe/Helsinki' },
  { label:'Athens, Bucharest  (GMT+2/+3)', value:'Europe/Athens' },
  { label:'Istanbul  (GMT+3)', value:'Europe/Istanbul' },
  { label:'Moscow  (GMT+3)', value:'Europe/Moscow' },
  { label:'Casablanca  (GMT+0/+1)', value:'Africa/Casablanca' },
  { label:'Lagos, Accra  (GMT+1)', value:'Africa/Lagos' },
  { label:'Cairo, Johannesburg  (GMT+2)', value:'Africa/Cairo' },
  { label:'Nairobi  (GMT+3)', value:'Africa/Nairobi' },
  { label:'Riyadh, Baghdad  (GMT+3)', value:'Asia/Riyadh' },
  { label:'Tehran  (GMT+3:30/+4:30)', value:'Asia/Tehran' },
  { label:'Dubai, Abu Dhabi  (GMT+4)', value:'Asia/Dubai' },
  { label:'Karachi, Islamabad  (GMT+5)', value:'Asia/Karachi' },
  { label:'Mumbai, New Delhi  (GMT+5:30)', value:'Asia/Kolkata' },
  { label:'Colombo  (GMT+5:30)', value:'Asia/Colombo' },
  { label:'Kathmandu  (GMT+5:45)', value:'Asia/Kathmandu' },
  { label:'Dhaka  (GMT+6)', value:'Asia/Dhaka' },
  { label:'Yangon  (GMT+6:30)', value:'Asia/Rangoon' },
  { label:'Bangkok, Hanoi, Jakarta  (GMT+7)', value:'Asia/Bangkok' },
  { label:'Singapore, KL, Manila  (GMT+8)', value:'Asia/Singapore' },
  { label:'Hong Kong, Taipei  (GMT+8)', value:'Asia/Hong_Kong' },
  { label:'Shanghai, Beijing  (GMT+8)', value:'Asia/Shanghai' },
  { label:'Perth  (GMT+8)', value:'Australia/Perth' },
  { label:'Tokyo, Seoul, Osaka  (GMT+9)', value:'Asia/Tokyo' },
  { label:'Brisbane  (GMT+10)', value:'Australia/Brisbane' },
  { label:'Sydney, Melbourne  (GMT+10/+11)', value:'Australia/Sydney' },
  { label:'Auckland, Wellington  (GMT+12/+13)', value:'Pacific/Auckland' },
  { label:'Buenos Aires, Santiago  (GMT-3)', value:'America/Argentina/Buenos_Aires' },
  { label:'Sao Paulo, Brasilia  (GMT-3)', value:'America/Sao_Paulo' },
  { label:'Lima, Bogota, Quito  (GMT-5)', value:'America/Lima' },
  { label:'New York, Miami, Toronto  (GMT-5/-4)', value:'America/New_York' },
  { label:'Mexico City  (GMT-6/-5)', value:'America/Mexico_City' },
  { label:'Chicago  (GMT-6/-5)', value:'America/Chicago' },
  { label:'Denver  (GMT-7/-6)', value:'America/Denver' },
  { label:'Phoenix  (GMT-7)', value:'America/Phoenix' },
  { label:'Los Angeles, Vancouver  (GMT-8/-7)', value:'America/Los_Angeles' },
  { label:'Honolulu  (GMT-10)', value:'Pacific/Honolulu' },
];

const AVATAR_COLORS = [
  { color:'#314F5C', text:'#AFE1F6', label:'Teal' }, { color:'#6F8FA7', text:'#ffffff', label:'Blue' },
  { color:'#819D84', text:'#ffffff', label:'Sage' }, { color:'#9F7E4F', text:'#ffffff', label:'Brown' },
  { color:'#B99C6D', text:'#ffffff', label:'Tan' }, { color:'#204C2C', text:'#ffffff', label:'Forest' },
  { color:'#5a7a8a', text:'#ffffff', label:'Slate' }, { color:'#2d6a94', text:'#ffffff', label:'Indigo' },
];

const PERMISSIONS = [
  { label:'Overview & Sana analytics', admin:true, data:true, support:true, marketing:true },
  { label:'Revenue & LTV', admin:true, data:false, support:false, marketing:true },
  { label:'Data quality & editor', admin:true, data:true, support:false, marketing:false },
  { label:'Data pipeline & geocoding', admin:true, data:true, support:false, marketing:false },
  { label:'Customer Help', admin:true, data:false, support:true, marketing:false },
  { label:'Growth & marketing screens', admin:true, data:false, support:false, marketing:true },
  { label:'Fraud & abuse', admin:true, data:false, support:false, marketing:false },
  { label:'App management', admin:true, data:false, support:false, marketing:false },
  { label:'Roles & access', admin:true, data:false, support:false, marketing:false },
  { label:'Activity log', admin:true, data:true, support:true, marketing:true },
];

const timeAgo = ts => { if (!ts) return 'Never'; const ms = Date.now() - new Date(ts); if (ms < 3600000) return Math.round(ms / 60000) + 'm ago'; if (ms < 86400000) return Math.round(ms / 3600000) + 'h ago'; return Math.round(ms / 86400000) + 'd ago'; };

function InlineField({ value, onSave, placeholder, style = {}, inputStyle = {} }) {
  const { t } = useConsoleTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);
  useEffect(() => { setDraft(value); }, [value]);
  function commit() { setEditing(false); const trimmed = (draft || '').trim(); if (trimmed && trimmed !== value) onSave(trimmed); else setDraft(value); }
  if (editing) return (
    <input ref={inputRef} value={draft || ''} onChange={e => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
      style={{ border:`1px solid ${t.accent}`, borderRadius:6, padding:'2px 7px', fontSize:'0.92rem', fontFamily:F, color:t.tx, background:t.rowbg, outline:'none', boxShadow:`0 0 0 3px ${t.accent}22`, ...inputStyle }} />
  );
  return (
    <span onClick={() => setEditing(true)} title="Click to edit"
      style={{ cursor:'text', display:'inline-flex', alignItems:'center', gap:5, borderRadius:5, padding:'1px 4px', ...style }}>
      {value || <span style={{ color:t.mut, fontStyle:'italic' }}>{placeholder}</span>}
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={t.mut} strokeWidth="2.5" strokeLinecap="round" style={{ opacity:0.6, flexShrink:0 }}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
    </span>
  );
}

function AvatarEditor({ initials, color, textColor, onSave }) {
  const { t } = useConsoleTheme();
  const [open, setOpen] = useState(false);
  const [draftIni, setDraftIni] = useState(initials);
  const [draftCol, setDraftCol] = useState(color);
  const [draftTxt, setDraftTxt] = useState(textColor);
  const ref = useRef(null);
  useEffect(() => { if (!open) return; function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); } document.addEventListener('mousedown', handler); return () => document.removeEventListener('mousedown', handler); }, [open]);
  function save() { const ini = draftIni.trim().slice(0, 2).toUpperCase(); onSave({ initials: ini || initials, color: draftCol, textColor: draftTxt }); setOpen(false); }
  return (
    <div style={{ position:'relative', flexShrink:0 }} ref={ref}>
      <div onClick={() => { setDraftIni(initials); setDraftCol(color); setOpen(v => !v); }} title="Edit avatar"
        style={{ width:38, height:38, borderRadius:'50%', background:color, color:textColor, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', fontWeight:700, cursor:'pointer', border: open ? `2px solid ${t.accent}` : '2px solid transparent', userSelect:'none' }}>
        {initials}
      </div>
      {open && (
        <div style={{ position:'absolute', left:0, top:46, background:t.panel, border:`1px solid ${t.bd}`, borderRadius:12, padding:14, zIndex:200, boxShadow:'0 8px 32px rgba(0,0,0,0.3)', width:214, fontFamily:F }}>
          <div style={{ fontSize:11, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:7 }}>Initials</div>
          <input value={draftIni} onChange={e => setDraftIni(e.target.value.toUpperCase().slice(0, 2))} maxLength={2}
            style={{ width:'100%', boxSizing:'border-box', border:`1px solid ${t.bd}`, borderRadius:7, padding:'6px 10px', fontSize:15, fontFamily:F, color:t.tx, background:t.rowbg, outline:'none', marginBottom:12, letterSpacing:'.1em', fontWeight:700, textAlign:'center' }} />
          <div style={{ fontSize:11, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Colour</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6, marginBottom:14 }}>
            {AVATAR_COLORS.map(c => (
              <div key={c.color} onClick={() => { setDraftCol(c.color); setDraftTxt(c.text); }} title={c.label}
                style={{ width:38, height:38, borderRadius:'50%', background:c.color, cursor:'pointer', border: draftCol === c.color ? `3px solid ${t.tx}` : '2px solid transparent', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.62rem', fontWeight:700, color:c.text }}>
                {draftIni || '??'}
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <button onClick={() => setOpen(false)} style={{ flex:1, padding:6, border:`1px solid ${t.bd}`, borderRadius:7, background:'transparent', fontSize:12, color:t.mut, cursor:'pointer', fontFamily:F }}>Cancel</button>
            <button onClick={save} style={{ flex:1, padding:6, border:'none', borderRadius:7, background:t.accent, fontSize:12, color: t.bg === '#f2f1ef' ? '#fff' : '#06222e', fontWeight:600, cursor:'pointer', fontFamily:F }}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Body() {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const onlineUsers = usePresenceContext();
  const [members, setMembers] = useState([]);
  const [authMap, setAuthMap] = useState({});
  const [me, setMe] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [pending, setPending] = useState(getPendingInvites);
  const [saving, setSaving] = useState({});
  const [roleFilter, setRoleFilter] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [showPerms, setShowPerms] = useState(false);

  function showToast(msg, type) { setToast({ msg, type: type || 'ok' }); setTimeout(() => setToast(null), 3500); }

  async function loadMembers() {
    try {
      const [{ data: tm }, { data: authData }, { data: { user } }] = await Promise.all([
        supabase.from('team_members').select('*').eq('active', true).order('created_at'),
        supabase.rpc('admin_get_auth_users'),
        supabase.auth.getUser(),
      ]);
      setMembers(tm || []);
      const map = {}; (authData || []).forEach(u => { map[u.email] = u; }); setAuthMap(map);
      setMe(user?.email || '');
    } catch (_e) { console.error('Roles error:', _e); }
    setLoading(false);
  }
  useEffect(() => { loadMembers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveField(email, updates) {
    setSaving(s => ({ ...s, [email]: true }));
    try {
      const { error } = await supabase.from('team_members').update(updates).eq('email', email);
      if (error) throw error;
      setMembers(prev => prev.map(m => m.email === email ? { ...m, ...updates } : m));
      showToast('Saved');
    } catch (_e) { showToast('Could not save — check team_members RLS policy', 'warn'); }
    setSaving(s => ({ ...s, [email]: false }));
  }

  async function saveEmail(member, newEmail) {
    if (!newEmail || newEmail === member.email) return;
    setSaving(s => ({ ...s, [member.email]: true }));
    try {
      const authUser = authMap[member.email];
      if (!authUser) {
        await supabase.from('team_members').update({ email: newEmail }).eq('email', member.email);
        setMembers(prev => prev.map(m => m.email === member.email ? { ...m, email: newEmail } : m));
        showToast('Email updated in roster (no auth account — resend invite if needed)', 'warn');
        setSaving(s => ({ ...s, [member.email]: false })); return;
      }
      const res = await fetch('/api/update-member', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ userId: authUser.id, email: newEmail }) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to update email');
      await supabase.from('team_members').update({ email: newEmail }).eq('email', member.email);
      setMembers(prev => prev.map(m => m.email === member.email ? { ...m, email: newEmail } : m));
      setAuthMap(prev => { const entry = prev[member.email]; if (!entry) return prev; const n = { ...prev }; delete n[member.email]; n[newEmail] = { ...entry, email: newEmail }; return n; });
      showToast('Email updated');
    } catch (e) { showToast(e.message || 'Failed to update email', 'warn'); }
    setSaving(s => ({ ...s, [member.email]: false }));
  }

  async function handleRemove(member) {
    setRemoveLoading(true);
    try {
      await supabase.from('team_members').update({ active: false }).eq('email', member.email);
      setMembers(prev => prev.filter(m => m.email !== member.email));
      const authUser = authMap[member.email];
      if (authUser) { const res = await fetch('/api/remove-member', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ userId: authUser.id }) }); if (!res.ok) { const r = await res.json(); throw new Error(r.error || 'Failed'); } }
      showToast(member.name + ' removed from team');
    } catch (e) { showToast(e.message || 'Failed to remove member', 'warn'); }
    finally { setRemoveLoading(false); setRemoving(null); }
  }

  const roleCounts = ROLES.reduce((acc, r) => ({ ...acc, [r]: 0 }), {});
  members.forEach(m => { roleCounts[m.role?.toLowerCase()] = (roleCounts[m.role?.toLowerCase()] || 0) + 1; });
  const roleKey = role => (role || 'admin').toLowerCase();

  const onlineCount = Object.keys(onlineUsers).filter(init => members.some(m => m.initials === init)).length;
  const lastSignIn = loading || !Object.keys(authMap).length ? '—' : timeAgo(Object.values(authMap).filter(u => members.some(m => m.email === u.email)).sort((a, b) => new Date(b.last_sign_in_at || 0) - new Date(a.last_sign_in_at || 0))[0]?.last_sign_in_at);

  const filteredMembers = members.filter(m => {
    const matchRole = !roleFilter || (m.role || 'admin').toLowerCase() === roleFilter;
    const q = memberSearch.trim().toLowerCase();
    const matchSearch = !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
    return matchRole && matchSearch;
  });

  return (
    <div>
      {toast && (
        <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background: toast.type === 'warn' ? t.warn : t.up, color:'#06222e', padding:'10px 20px', borderRadius:10, fontSize:'0.92rem', fontWeight:600, zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.3)', maxWidth:400, textAlign:'center' }}>{toast.msg}</div>
      )}

      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3,1fr)', gap:10 }}>
        <KPI label="Team members" value={loading ? '…' : members.length} sub="total" accent="#314F5C" />
        <KPI label="Online now" value={onlineCount} sub="of your team" subColor={onlineCount > 0 ? t.up : t.mut} accent="#5dcaa5" />
        <KPI label="Last sign-in" value={lastSignIn} sub="most recent" accent="#6F8FA7" />
      </div>

      <div style={{ display:'flex', gap:6, flexWrap:'wrap', margin:'16px 2px', alignItems:'center' }}>
        <button onClick={() => setRoleFilter(null)}
          style={{ padding:'4px 12px', borderRadius:20, border:`1px solid ${roleFilter === null ? t.accent : t.bd}`, background: roleFilter === null ? t.accent : t.period, fontSize:'0.85rem', color: roleFilter === null ? (t.bg === '#f2f1ef' ? '#fff' : '#06222e') : t.mut, fontWeight:600, cursor:'pointer', fontFamily:F }}>
          All <span style={{ opacity:0.7, marginLeft:2 }}>{members.length}</span>
        </button>
        {ROLES.map(r => {
          const isActive = roleFilter === r; const rc = ROLE_COLORS[r] || t.accent;
          return (
            <button key={r} onClick={() => setRoleFilter(isActive ? null : r)}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 11px', borderRadius:20, border:`1px solid ${isActive ? rc : rc + '55'}`, background: isActive ? rc : rc + '18', fontSize:'0.85rem', color: isActive ? '#fff' : rc, fontWeight:500, cursor:'pointer', fontFamily:F }}>
              <span>{ROLE_LABELS[r]}</span>
              <span style={{ background: isActive ? 'rgba(255,255,255,0.25)' : rc + '33', borderRadius:10, padding:'0 5px', fontWeight:700 }}>{loading ? '…' : (roleCounts[r] || 0)}</span>
            </button>
          );
        })}
      </div>

      <Panel title="◈ Team members" right={<button onClick={() => setInviteOpen(true)} style={{ fontSize:'0.9rem', padding:'6px 14px', borderRadius:8, border:'none', background:t.accent, color: t.bg === '#f2f1ef' ? '#fff' : '#06222e', fontWeight:600, cursor:'pointer', fontFamily:F }}>+ Invite</button>}>
        {members.length > 3 && (
          <input value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Search members…"
            style={{ width:'100%', boxSizing:'border-box', marginBottom:12, padding:'7px 11px', border:`1px solid ${t.bd}`, borderRadius:8, fontSize:'0.95rem', fontFamily:F, color:t.tx, background:t.rowbg, outline:'none' }} />
        )}
        {loading ? <Empty>Loading team…</Empty> : members.length === 0 ? <Empty>No members in the team_members table yet.</Empty>
          : filteredMembers.map(m => {
              const isSelf = m.email === me; const isSav = saving[m.email]; const authUser = authMap[m.email];
              const online = onlineUsers[m.initials]; const rk = roleKey(m.role); const rColor = ROLE_COLORS[rk] || t.mut;
              return (
                <div key={m.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 0', borderBottom:`.5px solid ${t.bd}`, flexWrap:'wrap', gap:8, opacity: isSav ? 0.6 : 1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <AvatarEditor initials={m.initials || '??'} color={m.color || '#314F5C'} textColor={m.text_color || '#fff'}
                      onSave={({ initials, color, textColor }) => saveField(m.email, { initials, color, text_color: textColor })} />
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                        <InlineField value={m.name} placeholder="Add name…" onSave={val => saveField(m.email, { name: val })} style={{ fontSize:'0.98rem', fontWeight:600, color:t.tx }} inputStyle={{ fontSize:'0.98rem', fontWeight:600 }} />
                        {isSelf && <span style={{ fontSize:'0.72rem', color:t.mut, flexShrink:0 }}>you</span>}
                        {online && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:t.live, border:`1px solid ${t.up}55`, borderRadius:99, padding:'1px 7px', fontSize:'0.72rem', fontWeight:600, color:t.livec, flexShrink:0 }}>
                            <span style={{ width:5, height:5, borderRadius:'50%', background:t.up, display:'inline-block' }} />
                            {online?.screen ? (SCREEN_LABELS[online.screen] || online.screen) : 'Online'}
                          </span>
                        )}
                      </div>
                      <InlineField value={m.email} placeholder="Email…" onSave={val => saveEmail(m, val)} style={{ fontSize:'0.82rem', color:t.mut }} inputStyle={{ fontSize:'0.82rem' }} />
                      <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:3 }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={t.mut} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 000 20M2 12h20"/></svg>
                        <select value={m.timezone || ''} onChange={e => saveField(m.email, { timezone: e.target.value || null })}
                          style={{ fontSize:'0.78rem', color: m.timezone ? t.accent : t.mut, background:'none', border:'none', cursor:'pointer', fontFamily:F, padding:0, outline:'none' }}>
                          <option value="">Set timezone…</option>
                          {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                        </select>
                      </div>
                      <div style={{ fontSize:'0.78rem', color:t.mut, marginTop:3, display:'flex', gap:8 }}>
                        {authUser?.created_at && <span>Joined {new Date(authUser.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</span>}
                        {authUser?.last_sign_in_at && <span>· Last seen {timeAgo(authUser.last_sign_in_at)}</span>}
                        {!authUser && <span style={{ color:t.warn }}>Pending sign-in</span>}
                      </div>
                    </div>
                  </div>
                  {isSelf ? (
                    <span style={{ fontSize:'0.82rem', fontWeight:700, padding:'4px 11px', borderRadius:8, background:rColor + '22', color:rColor, border:`1px solid ${rColor}44` }}>{m.role || 'Admin'}</span>
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <select value={rk} onChange={e => saveField(m.email, { role: e.target.value })}
                        style={{ fontSize:'0.85rem', padding:'4px 8px', border:`1px solid ${rColor}55`, borderRadius:8, background:rColor + '18', color:rColor, fontWeight:600, cursor:'pointer', fontFamily:F }}>
                        {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                      <button onClick={() => setRemoving(m)} title="Remove member"
                        style={{ width:28, height:28, borderRadius:7, border:`1px solid ${t.danger}44`, background:t.danger + '14', color:t.danger, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
        <div style={{ marginTop:12, padding:'10px 12px', background:t.rowbg, borderRadius:8, fontSize:'0.82rem', color:t.mut, lineHeight:1.6 }}>
          Click any name, email, or avatar to edit inline. Members are sourced from the <strong>team_members</strong> table — add new members there or use Invite.
        </div>
      </Panel>

      {pending.length > 0 && (
        <>
          <SectionLabel right={`${pending.length}`}>Pending invites</SectionLabel>
          <Panel>
            {pending.map(inv => (
              <div key={inv.email} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:`.5px solid ${t.bd}` }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:(ROLE_COLORS[inv.role] || t.mut) + '22', border:`1px solid ${(ROLE_COLORS[inv.role] || t.mut)}55`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.72rem', color:ROLE_COLORS[inv.role] || t.mut, fontWeight:700, flexShrink:0 }}>{inv.email.slice(0, 2).toUpperCase()}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'0.92rem', color:t.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.email}</div>
                  <div style={{ fontSize:'0.78rem', color:t.mut, marginTop:1 }}>Invited {timeAgo(inv.sentAt)}</div>
                </div>
                <span style={{ fontSize:'0.8rem', fontWeight:600, padding:'2px 8px', borderRadius:6, background:(ROLE_COLORS[inv.role] || t.mut) + '22', color:ROLE_COLORS[inv.role] || t.mut, flexShrink:0 }}>{ROLE_LABELS[inv.role]}</span>
                <button onClick={async () => { try { const res = await fetch('/api/invite', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ email: inv.email, role: inv.role }) }); if (!res.ok) throw new Error('Failed'); const u = getPendingInvites().map(i => i.email === inv.email ? { ...i, sentAt: new Date().toISOString() } : i); savePendingInvites(u); setPending([...u]); showToast('Invite resent to ' + inv.email); } catch (_e) { showToast('Failed to resend invite', 'warn'); } }}
                  style={{ fontSize:'0.8rem', color:t.accent, background:'none', border:`1px solid ${t.bd}`, borderRadius:6, padding:'3px 9px', cursor:'pointer', fontFamily:F, flexShrink:0 }}>Resend</button>
                <button onClick={() => { removePendingInvite(inv.email); setPending(getPendingInvites()); showToast('Invite revoked', 'warn'); }}
                  style={{ fontSize:'0.8rem', color:t.danger, background:'none', border:`1px solid ${t.danger}44`, borderRadius:6, padding:'3px 9px', cursor:'pointer', fontFamily:F, flexShrink:0 }}>Revoke</button>
              </div>
            ))}
          </Panel>
        </>
      )}

      <SectionLabel right={<span onClick={() => setShowPerms(v => !v)} style={{ cursor:'pointer', color:t.mut }}>{showPerms ? 'Hide' : 'Show all'}</span>}>Permissions by role</SectionLabel>
      {showPerms && (
        <Panel>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.92rem' }}>
              <thead><tr>
                <th style={{ textAlign:'left', padding:'6px 8px', color:t.mut, fontWeight:600, borderBottom:`1px solid ${t.bd}` }}>Feature</th>
                {ROLES.map(r => <th key={r} style={{ padding:'6px 8px', color:ROLE_COLORS[r] || t.tx, fontWeight:700, borderBottom:`1px solid ${t.bd}`, textAlign:'center', whiteSpace:'nowrap' }}>{ROLE_LABELS[r]}</th>)}
              </tr></thead>
              <tbody>
                {PERMISSIONS.map((p, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : t.rowbg }}>
                    <td style={{ padding:'6px 8px', color:t.tx, borderBottom:`1px solid ${t.bd}` }}>{p.label}</td>
                    {ROLES.map(r => (
                      <td key={r} style={{ padding:'6px 8px', textAlign:'center', borderBottom:`1px solid ${t.bd}` }}>
                        {p[r] ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.up} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.bd} strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {Object.keys(authMap).length > 0 && (
        <>
          <SectionLabel>Recent activity</SectionLabel>
          <Panel>
            {Object.values(authMap).filter(u => u.last_sign_in_at).sort((a, b) => new Date(b.last_sign_in_at) - new Date(a.last_sign_in_at)).slice(0, 8).map((u, i, arr) => {
              const mem = members.find(m => m.email === u.email); const rk = (mem?.role || 'admin').toLowerCase(); const rColor = ROLE_COLORS[rk] || t.mut;
              const isOnline = onlineUsers[mem?.initials];
              return (
                <div key={u.email} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < arr.length - 1 ? `.5px solid ${t.bd}` : 'none' }}>
                  <div style={{ position:'relative', flexShrink:0 }}>
                    <div style={{ width:30, height:30, borderRadius:'50%', background:mem?.color || '#314F5C', color:mem?.text_color || '#AFE1F6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.72rem', fontWeight:700 }}>{mem?.initials || u.email.slice(0, 2).toUpperCase()}</div>
                    {isOnline && <div style={{ position:'absolute', bottom:0, right:0, width:8, height:8, borderRadius:'50%', background:t.up, border:`1.5px solid ${t.panel}` }} />}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'0.92rem', fontWeight:600, color:t.tx }}>{mem?.name || u.email.split('@')[0]}</div>
                    <div style={{ fontSize:'0.78rem', color:t.mut }}>{u.email}</div>
                  </div>
                  <span style={{ fontSize:'0.8rem', fontWeight:600, padding:'2px 7px', borderRadius:6, background:rColor + '22', color:rColor, flexShrink:0 }}>{ROLE_LABELS[rk] || 'Admin'}</span>
                  <span style={{ fontSize:'0.8rem', color:t.mut, flexShrink:0, minWidth:60, textAlign:'right' }}>{isOnline ? <span style={{ color:t.up, fontWeight:600 }}>Online</span> : timeAgo(u.last_sign_in_at)}</span>
                </div>
              );
            })}
          </Panel>
        </>
      )}

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onSuccess={() => setPending(getPendingInvites())} />}

      {removing && (
        <div onKeyDown={e => { if (e.key === 'Escape') setRemoving(null); }} tabIndex={-1} style={{ position:'fixed', inset:0, background:'rgba(5,12,20,0.55)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:t.panel, borderRadius:14, padding:24, maxWidth:380, width:'100%', boxShadow:'0 24px 64px rgba(0,0,0,0.4)', fontFamily:F, border:`1px solid ${t.bd}` }}>
            <div style={{ fontSize:'1.05rem', fontWeight:600, color:t.tx, marginBottom:8 }}>Remove team member</div>
            <div style={{ fontSize:'0.95rem', color:t.mut, marginBottom:16, lineHeight:1.5 }}>Remove <strong style={{ color:t.tx }}>{removing.name}</strong> from the dashboard? They will lose access immediately.</div>
            <div style={{ fontSize:'0.95rem', fontWeight:500, color:t.tx, marginBottom:4 }}>{removing.email}</div>
            <div style={{ fontSize:'0.82rem', color:t.mut, marginBottom:20 }}>{removing.role}</div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setRemoving(null)} disabled={removeLoading} style={{ padding:'7px 16px', background:'transparent', border:`1px solid ${t.bd}`, borderRadius:8, fontSize:13, color:t.mut, cursor:'pointer', fontFamily:F }}>Cancel</button>
              <button onClick={() => handleRemove(removing)} disabled={removeLoading} style={{ padding:'7px 16px', background:t.danger, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor: removeLoading ? 'not-allowed' : 'pointer', fontFamily:F, opacity: removeLoading ? 0.6 : 1 }}>{removeLoading ? 'Removing…' : 'Remove member'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Roles(props) {
  if (props && props.embed) return <Body />;
  return <ConsoleShell title="Roles & Access"><Body /></ConsoleShell>;
}
